import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRBAC } from '../../auth/useRBAC';
import { stableDocsSettingsControls } from '../../auth/pageControls';
import { ProfileSettings, PracticeSettings, IntegrationsSettings, SubscriptionSettings, PrintersSettings } from './';
import { UserManagement } from './UserManagement';
import ServicePricing from './ServicePricing';
import BillingSettings from './BillingSettings';
import RemindersSettings from './RemindersSettings';
import TemplateEditor from './TemplateEditor';
import OfflineSettings from './OfflineSettings';
import StableDocsSettings from './StableDocsSettings';
import { OFFLINE_AVAILABLE } from '../../offline/entities';

interface NavItem {
  key: string;
  label: string;
  subtitle?: string;
  requiredPermission?: string;
}

const SIDEBAR_WIDTH = 220;

const Settings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const rbac = useRBAC();
  const { hasPermission, claims } = rbac;
  const canEditStableDocs = stableDocsSettingsControls(rbac).edit;
  // The ?tab= URL param is the ONLY source of truth for the active tab —
  // deep links (onboarding checklist, QBO/Stripe returns) and sidebar clicks
  // go through the same door. Keeping a second copy in local state synced by
  // an effect is what caused the "tab snaps back and wipes the form" bug:
  // with ?tab=practice in the URL, every re-render re-asserted the param.
  const [searchParams, setSearchParams] = useSearchParams();

  // Pricing/invoicing settings are clinic
  // configuration — OWNER/ADMIN only. A permission check can't gate this:
  // every role holds view:billing (needed for day-to-day invoicing), so gate
  // by role, mirroring the backend @Roles on the write endpoints.
  const isClinicAdmin = claims?.role === 'OWNER' || claims?.role === 'ADMIN';

  const navItems: NavItem[] = useMemo(() => {
    const items: Omit<NavItem, 'tabIndex'>[] = [
      { key: 'account', label: 'Account settings', subtitle: 'Your profile' },
      { key: 'practice', label: 'Practice settings', subtitle: 'Details, logo' },
      { key: 'integrations', label: 'Integrations', subtitle: 'Connected services' },
    ];

    if (isClinicAdmin) {
      items.push({ key: 'service-pricing', label: 'Service Pricing', subtitle: 'Services' });
      items.push({ key: 'billing', label: 'Invoicing Settings', subtitle: 'Invoice defaults & payment methods' });
      // Backend PUT is @Roles(OWNER, ADMIN) — same gate as the other clinic config.
      items.push({ key: 'reminders', label: 'Client Reminders', subtitle: 'Care and appointment emails' });
    }

    items.push({ key: 'templates', label: 'SOAP Templates', subtitle: 'Structured exam templates' });
    items.push({ key: 'printers', label: 'Printers', subtitle: 'Label printer setup & test' });
    items.push({ key: 'subscription', label: 'Subscription', subtitle: 'Plan & billing cycle' });

    // PUT /stabledocs/settings declares manage:practice_settings.
    if (canEditStableDocs) {
      items.push({ key: 'stabledocs', label: 'StableDocs', subtitle: 'Sharing records' });
    }

    // Offline mode opt-in — only shown when the feature is available in this
    // build (REACT_APP_OFFLINE_MODE). Per-device, so every role can self-enable.
    if (OFFLINE_AVAILABLE) {
      items.push({ key: 'offline', label: 'Offline mode', subtitle: 'Use StableTrack without internet' });
    }

    if (hasPermission('manage:users')) {
      items.push({ key: 'team', label: 'Team members', subtitle: 'Manage staff & roles' });
    }

    return items;
  }, [hasPermission, isClinicAdmin, canEditStableDocs]);

  // Unknown/absent ?tab (or a gated tab before claims load) falls back to the
  // first item; once claims land, navItems grows and the match self-heals.
  const tabParam = searchParams.get('tab');
  const selectedItem = navItems.find((item) => item.key === tabParam) ?? navItems[0];
  // replace — tab clicks don't stack history entries, so Back leaves Settings
  // (the behavior the old local-state tabs had).
  const selectTab = (key: string) => setSearchParams({ tab: key }, { replace: true });

  const renderContent = () => {
    switch (selectedItem.key) {
      case 'account': return <ProfileSettings />;
      case 'practice': return <PracticeSettings />;
      case 'integrations': return <IntegrationsSettings />;
      case 'service-pricing': return <ServicePricing />;
      case 'billing': return <BillingSettings />;
      case 'reminders': return <RemindersSettings />;
      case 'templates': return <TemplateEditor />;
      case 'printers': return <PrintersSettings />;
      case 'subscription': return <SubscriptionSettings />;
      case 'stabledocs': return <StableDocsSettings />;
      case 'team': return <UserManagement />;
      case 'offline': return <OfflineSettings />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 } }}>
      {/* Breadcrumb navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2.5,
          gap: 1,
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          size="small"
          sx={{ color: 'text.secondary', mr: 0.5 }}
          aria-label="Back to dashboard"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          Workspace settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          &gt;
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
          {selectedItem?.label ?? 'Settings'}
        </Typography>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 } }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: { xs: '100%', md: SIDEBAR_WIDTH },
            minWidth: { md: SIDEBAR_WIDTH },
            flexShrink: 0,
          }}
        >
          {isMobile ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                p: 1,
                bgcolor: 'background.paper',
                overflowX: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5, minWidth: 'max-content' }}>
                {navItems.map((item) => {
                  const isSelected = selectedItem.key === item.key;
                  return (
                    <Box
                      key={item.key}
                      onClick={() => selectTab(item.key)}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                        color: isSelected ? '#fff' : 'text.primary',
                        fontSize: '0.8rem',
                        fontWeight: isSelected ? 600 : 400,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        },
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                py: 1,
                px: 0.5,
                bgcolor: 'background.paper',
                position: 'sticky',
                top: 80,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, px: 1.5, pt: 1, pb: 1.5, fontSize: '1rem' }}
              >
                Settings
              </Typography>
              <List disablePadding dense>
                {navItems.map((item) => {
                  const isSelected = selectedItem.key === item.key;
                  return (
                    <ListItemButton
                      key={item.key}
                      selected={isSelected}
                      onClick={() => selectTab(item.key)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.25,
                        py: 0.75,
                        px: 1.5,
                        '&.Mui-selected': {
                          bgcolor: 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                        },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        secondary={item.subtitle}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'primary.main' : 'text.primary',
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          sx: { mt: 0.15 },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>

        {/* Content area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              bgcolor: 'background.paper',
            }}
          >
            {renderContent()}
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Settings;
